import { useEffect, useState, useRef } from "react";
import { API, useCurrency, useAuth } from "../../App";
import AdminLayout from "../../components/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Plus, Pencil, Trash2, Search, Star, Loader2, Upload, X, Image as ImageIcon, Leaf, Flame, SlidersHorizontal, ChevronDown, ChevronRight, Copy, ToggleLeft, ToggleRight, ChefHat, FileDown, FileSpreadsheet, Dumbbell } from "lucide-react";
import { toast } from "sonner";

export default function AdminMenu() {
  const { symbol } = useCurrency();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isChefOrManager = user?.role === "chef" || user?.role === "manager";
  const isManager = user?.role === "manager";
  const [menuItems, setMenuItems] = useState([]);
  const [taxGroups, setTaxGroups] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const fileInputRef = useRef(null);
  const [showFilters, setShowFilters] = useState(false);
  const [collapsedCategories, setCollapsedCategories] = useState({});

  // Kitchen selection for managers
  const [kitchens, setKitchens] = useState([]);
  const [selectedKitchenId, setSelectedKitchenId] = useState("");

  // Advanced filter state
  const [filters, setFilters] = useState({
    name: "",
    category: "all",
    status: "all",
    type: "all",
    bestseller: "all",
    priceMin: "",
    priceMax: "",
  });

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    base_price: "",
    category_id: "",
    subcategory_id: "",
    image_url: "",
    image_path: "",
    is_available: true,
    is_vegetarian: false,
    is_bestseller: false,
    is_gym_special: false,
    tax_group_id: "",
    nutritional_info: { protein: "", fat: "", carbs: "", calories: "" },
    superfood_angle: "",
    superfood_name: "",
    variations: [],
    addon_groups: [],
    cross_sell_ids: [],
    upsell_ids: [],
  });

  useEffect(() => {
    if (isManager) {
      fetchKitchens();
    } else {
      fetchMenu();
    }
    fetchTaxGroups();
    fetchCategories();
  }, []);

  const fetchKitchens = async () => {
    try {
      const res = await fetch(`${API}/kitchens`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setKitchens(data);
        if (data.length > 0) {
          setSelectedKitchenId(data[0].kitchen_id);
          fetchMenu(data[0].kitchen_id);
        } else {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error("Failed to fetch kitchens:", error);
      setLoading(false);
    }
  };

  const handleKitchenChange = (kitchenId) => {
    setSelectedKitchenId(kitchenId);
    setLoading(true);
    fetchMenu(kitchenId);
  };

  const fetchMenu = async (kitchenId) => {
    try {
      const url = kitchenId ? `${API}/menu/all?kitchen_id=${kitchenId}` : `${API}/menu/all`;
      const response = await fetch(url, { credentials: "include" });
      if (response.ok) setMenuItems(await response.json());
    } catch (error) {
      console.error("Failed to fetch menu:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTaxGroups = async () => {
    try {
      const response = await fetch(`${API}/tax-groups/active`, { credentials: "include" });
      if (response.ok) setTaxGroups(await response.json());
    } catch (error) {
      console.error("Failed to fetch tax groups:", error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API}/categories/all`, { credentials: "include" });
      if (response.ok) setCategories(await response.json());
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  };

  const parentCategories = categories.filter(c => !c.parent_id);
  const getSubcategories = (parentId) => categories.filter(c => c.parent_id === parentId);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Allowed: JPEG, PNG, WebP, GIF");
      return;
    }

    if (file.size > 25 * 1024 * 1024) {
      toast.error("File too large. Max size: 25MB");
      return;
    }

    const img = new window.Image();
    img.onload = async () => {
      if (img.width > 1800 || img.height > 1200) {
        toast.error(`Image dimensions too large. Max: 1800x1200px, Got: ${img.width}x${img.height}px`);
        return;
      }

      setUploadingImage(true);
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);

      try {
        const response = await fetch(`${API}/upload/image`, {
          method: "POST",
          credentials: "include",
          body: formDataUpload,
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || "Upload failed");
        }

        const result = await response.json();
        setFormData((prev) => ({ ...prev, image_path: result.path, image_url: result.url }));
        toast.success("Image uploaded successfully");
      } catch (error) {
        toast.error(error.message || "Failed to upload image");
      } finally {
        setUploadingImage(false);
      }
    };
    img.src = URL.createObjectURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    const nutritionalInfo = {
      protein: parseFloat(formData.nutritional_info.protein) || 0,
      fat: parseFloat(formData.nutritional_info.fat) || 0,
      carbs: parseFloat(formData.nutritional_info.carbs) || 0,
      calories: parseFloat(formData.nutritional_info.calories) || 0,
    };

    const payload = {
      ...formData,
      base_price: parseFloat(formData.base_price),
      tax_group_id: formData.tax_group_id === "none" ? null : formData.tax_group_id || null,
      category_id: formData.category_id === "none" ? null : formData.category_id || null,
      subcategory_id: formData.subcategory_id === "none" ? null : formData.subcategory_id || null,
      nutritional_info: nutritionalInfo,
    };

    try {
      const url = editingItem ? `${API}/menu/${editingItem.item_id}` : `${API}/menu`;
      const method = editingItem ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error("Failed to save item");

      toast.success(editingItem ? "Item updated!" : "Item created!");
      setIsDialogOpen(false);
      resetForm();
      fetchMenu(isManager ? selectedKitchenId : undefined);
    } catch (error) {
      toast.error("Failed to save item");
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (itemId) => {
    if (!confirm("Are you sure you want to delete this item?")) return;

    try {
      const response = await fetch(`${API}/menu/${itemId}`, { method: "DELETE", credentials: "include" });
      if (!response.ok) throw new Error("Failed to delete");
      toast.success("Item deleted!");
      fetchMenu(isManager ? selectedKitchenId : undefined);
    } catch (error) {
      toast.error("Failed to delete item");
    }
  };

  const handleDuplicate = async (itemId) => {
    try {
      const response = await fetch(`${API}/menu/${itemId}/duplicate`, {
        method: "POST",
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to duplicate");
      toast.success("Item duplicated!");
      fetchMenu(isManager ? selectedKitchenId : undefined);
    } catch (error) {
      toast.error("Failed to duplicate item");
    }
  };

  const toggleAvailability = async (itemId) => {
    try {
      const item = menuItems.find((i) => i.item_id === itemId);
      // Chef/Manager: toggle kitchen-specific availability
      if (isChefOrManager && (item?.kitchen_id || selectedKitchenId)) {
        const kitId = item?.kitchen_id || selectedKitchenId;
        const newAvail = !(item.kitchen_available ?? item.is_available);
        const response = await fetch(`${API}/kitchen-menus/${kitId}/item/${itemId}/availability`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ is_available: newAvail }),
        });
        if (!response.ok) throw new Error("Failed to toggle");
        setMenuItems((prev) =>
          prev.map((i) => i.item_id === itemId ? { ...i, kitchen_available: newAvail, is_available: newAvail } : i)
        );
        toast.success(newAvail ? "Item now available in your kitchen" : "Item marked unavailable in your kitchen");
        return;
      }
      // Admin: toggle global availability
      const response = await fetch(`${API}/menu/${itemId}/toggle-availability`, {
        method: "PUT",
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to toggle");
      const result = await response.json();
      setMenuItems((prev) =>
        prev.map((i) =>
          i.item_id === itemId ? { ...i, is_available: result.is_available } : i
        )
      );
      toast.success(result.is_available ? "Item is now available" : "Item marked unavailable");
    } catch (error) {
      toast.error("Failed to toggle availability");
    }
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      description: item.description,
      base_price: item.base_price?.toString() || "",
      category_id: item.category_id || "",
      subcategory_id: item.subcategory_id || "",
      image_url: item.image_url || "",
      image_path: item.image_path || "",
      is_available: item.is_available,
      is_vegetarian: item.is_vegetarian,
      is_bestseller: item.is_bestseller,
      is_gym_special: !!item.is_gym_special,
      tax_group_id: item.tax_group_id || "",
      nutritional_info: item.nutritional_info || { protein: "", fat: "", carbs: "", calories: "" },
      superfood_angle: item.superfood_angle || "",
      superfood_name: item.superfood_name || "",
      variations: item.variations || [],
      addon_groups: item.addon_groups || [],
      cross_sell_ids: item.cross_sell_ids || [],
      upsell_ids: item.upsell_ids || [],
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({
      name: "", description: "", base_price: "", category_id: "", subcategory_id: "",
      image_url: "", image_path: "", is_available: true, is_vegetarian: false, is_bestseller: false, is_gym_special: false,
      tax_group_id: "", nutritional_info: { protein: "", fat: "", carbs: "", calories: "" },
      superfood_angle: "", superfood_name: "", variations: [], addon_groups: [], cross_sell_ids: [], upsell_ids: [],
    });
  };

  // Variation handlers
  const addVariation = () => {
    setFormData((prev) => ({
      ...prev,
      variations: [...prev.variations, { variation_id: `var_${Date.now()}`, name: "", options: [] }],
    }));
  };

  const updateVariation = (index, field, value) => {
    setFormData((prev) => {
      const variations = [...prev.variations];
      variations[index] = { ...variations[index], [field]: value };
      return { ...prev, variations };
    });
  };

  const removeVariation = (index) => {
    setFormData((prev) => ({ ...prev, variations: prev.variations.filter((_, i) => i !== index) }));
  };

  const addVariationOption = (varIndex) => {
    setFormData((prev) => {
      const variations = [...prev.variations];
      variations[varIndex].options = [
        ...variations[varIndex].options,
        { option_id: `opt_${Date.now()}`, value: "", price_adjustment: 0, is_available: true },
      ];
      return { ...prev, variations };
    });
  };

  const updateVariationOption = (varIndex, optIndex, field, value) => {
    setFormData((prev) => {
      const variations = [...prev.variations];
      variations[varIndex].options[optIndex] = {
        ...variations[varIndex].options[optIndex],
        [field]: field === "price_adjustment" ? parseFloat(value) || 0 : value,
      };
      return { ...prev, variations };
    });
  };

  const removeVariationOption = (varIndex, optIndex) => {
    setFormData((prev) => {
      const variations = [...prev.variations];
      variations[varIndex].options = variations[varIndex].options.filter((_, i) => i !== optIndex);
      return { ...prev, variations };
    });
  };

  // Addon group handlers
  const addAddonGroup = () => {
    setFormData((prev) => ({
      ...prev,
      addon_groups: [...prev.addon_groups, { group_id: `addongrp_${Date.now()}`, name: "", min_selection: 0, max_selection: 10, addons: [] }],
    }));
  };

  const updateAddonGroup = (index, field, value) => {
    setFormData((prev) => {
      const addon_groups = [...prev.addon_groups];
      addon_groups[index] = {
        ...addon_groups[index],
        [field]: field === "min_selection" || field === "max_selection" ? parseInt(value) || 0 : value,
      };
      return { ...prev, addon_groups };
    });
  };

  const removeAddonGroup = (index) => {
    setFormData((prev) => ({ ...prev, addon_groups: prev.addon_groups.filter((_, i) => i !== index) }));
  };

  const addAddon = (grpIndex) => {
    setFormData((prev) => {
      const addon_groups = [...prev.addon_groups];
      addon_groups[grpIndex].addons = [
        ...addon_groups[grpIndex].addons,
        { addon_id: `addon_${Date.now()}`, name: "", price: 0, is_free: false, is_available: true },
      ];
      return { ...prev, addon_groups };
    });
  };

  const updateAddon = (grpIndex, addonIndex, field, value) => {
    setFormData((prev) => {
      const addon_groups = [...prev.addon_groups];
      addon_groups[grpIndex].addons[addonIndex] = {
        ...addon_groups[grpIndex].addons[addonIndex],
        [field]: field === "price" ? parseFloat(value) || 0 : value,
      };
      return { ...prev, addon_groups };
    });
  };

  const removeAddon = (grpIndex, addonIndex) => {
    setFormData((prev) => {
      const addon_groups = [...prev.addon_groups];
      addon_groups[grpIndex].addons = addon_groups[grpIndex].addons.filter((_, i) => i !== addonIndex);
      return { ...prev, addon_groups };
    });
  };

  const filteredItems = menuItems.filter((item) => {
    if (filters.name && !item.name.toLowerCase().includes(filters.name.toLowerCase())) return false;
    if (filters.category !== "all" && (item.category_name || "Uncategorized") !== filters.category) return false;
    if (filters.status === "available" && !item.is_available) return false;
    if (filters.status === "unavailable" && item.is_available) return false;
    if (filters.type === "veg" && !item.is_vegetarian) return false;
    if (filters.type === "nonveg" && item.is_vegetarian) return false;
    if (filters.bestseller === "yes" && !item.is_bestseller) return false;
    if (filters.bestseller === "no" && item.is_bestseller) return false;
    if (filters.priceMin && (item.base_price || 0) < parseFloat(filters.priceMin)) return false;
    if (filters.priceMax && (item.base_price || 0) > parseFloat(filters.priceMax)) return false;
    return true;
  });

  // Group items by category
  const groupedItems = {};
  filteredItems.forEach((item) => {
    const cat = item.category_name || "Uncategorized";
    if (!groupedItems[cat]) groupedItems[cat] = [];
    groupedItems[cat].push(item);
  });
  const sortedCategoryNames = Object.keys(groupedItems).sort((a, b) => {
    if (a === "Uncategorized") return 1;
    if (b === "Uncategorized") return -1;
    return a.localeCompare(b);
  });

  const uniqueCategories = [...new Set(menuItems.map((i) => i.category_name || "Uncategorized"))].sort();

  const toggleCategory = (cat) => {
    setCollapsedCategories((prev) => ({ ...prev, [cat]: !prev[cat] }));
  };

  const activeFilterCount = Object.entries(filters).filter(([k, v]) => {
    if (k === "name") return v !== "";
    if (k === "priceMin" || k === "priceMax") return v !== "";
    return v !== "all";
  }).length;

  const clearFilters = () => {
    setFilters({ name: "", category: "all", status: "all", type: "all", bestseller: "all", priceMin: "", priceMax: "" });
  };

  return (
    <AdminLayout>
      <div className="animate-fade-in max-w-full overflow-x-hidden" data-testid="admin-menu">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Menu Management</h1>
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            {/* Kitchen selector for managers */}
            {isManager && kitchens.length > 0 && (
              <div className="flex items-center gap-2" data-testid="manager-kitchen-selector">
                <ChefHat size={18} className="text-orange-600" />
                <Select value={selectedKitchenId} onValueChange={handleKitchenChange}>
                  <SelectTrigger className="w-52" data-testid="manager-kitchen-select">
                    <SelectValue placeholder="Select kitchen" />
                  </SelectTrigger>
                  <SelectContent>
                    {kitchens.map((k) => (
                      <SelectItem key={k.kitchen_id} value={k.kitchen_id}>
                        {k.kitchen_code} - {k.city}{k.locality ? ` (${k.locality})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          {isAdmin && <Button type="button" variant="outline" onClick={() => setImportOpen(true)} className="hidden sm:flex" data-testid="menu-import-btn">
            <FileSpreadsheet size={16} className="mr-2" /> Import CSV
          </Button>}
          {isAdmin && <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="hidden sm:flex bg-green-500 hover:bg-green-600" data-testid="add-menu-item-btn">
                <Plus size={18} className="mr-2" /> Add Item
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
              <DialogHeader>
                <DialogTitle>{editingItem ? "Edit Item" : "Add New Item"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Info */}
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-4">Basic Information</h3>
                  <div className="space-y-4">
                    <div>
                      <Label>Product Name</Label>
                      <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="bg-white" placeholder="Enter product name" />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        required
                        className="bg-white resize-y min-h-[100px]"
                        placeholder="Describe your product..."
                        style={{ resize: "vertical" }}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Base Price</Label>
                        <Input type="number" step="0.01" value={formData.base_price} onChange={(e) => setFormData({ ...formData, base_price: e.target.value })} required className="bg-white" placeholder="0.00" />
                      </div>
                      <div>
                        <Label>Category</Label>
                        <Select value={formData.category_id || "none"} onValueChange={(value) => setFormData({ ...formData, category_id: value === "none" ? "" : value, subcategory_id: "" })}>
                          <SelectTrigger className="bg-white"><SelectValue placeholder="Select category" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No Category</SelectItem>
                            {parentCategories.map((cat) => (
                              <SelectItem key={cat.category_id} value={cat.category_id}>{cat.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {formData.category_id && getSubcategories(formData.category_id).length > 0 && (
                      <div>
                        <Label>Subcategory</Label>
                        <Select value={formData.subcategory_id || "none"} onValueChange={(value) => setFormData({ ...formData, subcategory_id: value === "none" ? "" : value })}>
                          <SelectTrigger className="bg-white"><SelectValue placeholder="Select subcategory" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No Subcategory</SelectItem>
                            {getSubcategories(formData.category_id).map((sub) => (
                              <SelectItem key={sub.category_id} value={sub.category_id}>{sub.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                </div>

                {/* Image Upload */}
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-4">Product Image</h3>
                  <input type="file" ref={fileInputRef} onChange={handleImageUpload} accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" />
                  {formData.image_url ? (
                    <div className="relative inline-block">
                      <img src={formData.image_url.startsWith("/api") ? `${API.replace("/api", "")}${formData.image_url}` : formData.image_url} alt="Product" className="w-32 h-32 object-cover rounded-lg border" />
                      <button type="button" onClick={() => setFormData({ ...formData, image_url: "", image_path: "" })} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"><X size={14} /></button>
                    </div>
                  ) : (
                    <div onClick={() => fileInputRef.current?.click()} className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-green-500 transition-colors">
                      {uploadingImage ? <Loader2 className="w-8 h-8 mx-auto text-green-500 animate-spin" /> : (
                        <>
                          <ImageIcon className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                          <p className="text-gray-600 text-sm">Click to upload image</p>
                          <p className="text-gray-400 text-xs mt-1">Max: 1800x1200px, 25MB</p>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Nutritional Info */}
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-4">Nutritional Information</h3>
                  <div className="grid grid-cols-4 gap-3">
                    <div>
                      <Label className="text-xs">Protein (g)</Label>
                      <Input type="number" step="0.1" value={formData.nutritional_info.protein} onChange={(e) => setFormData({ ...formData, nutritional_info: { ...formData.nutritional_info, protein: e.target.value } })} className="bg-white" placeholder="0" />
                    </div>
                    <div>
                      <Label className="text-xs">Fat (g)</Label>
                      <Input type="number" step="0.1" value={formData.nutritional_info.fat} onChange={(e) => setFormData({ ...formData, nutritional_info: { ...formData.nutritional_info, fat: e.target.value } })} className="bg-white" placeholder="0" />
                    </div>
                    <div>
                      <Label className="text-xs">Carbs (g)</Label>
                      <Input type="number" step="0.1" value={formData.nutritional_info.carbs} onChange={(e) => setFormData({ ...formData, nutritional_info: { ...formData.nutritional_info, carbs: e.target.value } })} className="bg-white" placeholder="0" />
                    </div>
                    <div>
                      <Label className="text-xs">Calories (kcal)</Label>
                      <Input type="number" step="1" value={formData.nutritional_info.calories} onChange={(e) => setFormData({ ...formData, nutritional_info: { ...formData.nutritional_info, calories: e.target.value } })} className="bg-white" placeholder="0" />
                    </div>
                  </div>
                </div>

                {/* Superfood Angle */}
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Leaf className="w-5 h-5 text-green-500" /> Superfood Angle
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-sm text-gray-700 mb-1">What's the superfood?</Label>
                      <Input
                        value={formData.superfood_name}
                        onChange={(e) => setFormData({ ...formData, superfood_name: e.target.value })}
                        className="bg-white"
                        placeholder="E.g., Quinoa, Chia Seeds, Spirulina..."
                        data-testid="superfood-name-input"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-gray-700 mb-1">Superfood Angle</Label>
                      <Textarea
                        value={formData.superfood_angle}
                        onChange={(e) => setFormData({ ...formData, superfood_angle: e.target.value })}
                        className="bg-white resize-y"
                        placeholder="E.g., Rich in antioxidants, High protein, Omega-3 fatty acids..."
                        style={{ resize: "vertical" }}
                        data-testid="superfood-angle-input"
                      />
                    </div>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1">This will be highlighted on the user interface</p>
                </div>

                {/* Tax Group */}
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-4">Tax Configuration</h3>
                  <Select value={formData.tax_group_id || "none"} onValueChange={(value) => setFormData({ ...formData, tax_group_id: value === "none" ? "" : value })}>
                    <SelectTrigger className="bg-white"><SelectValue placeholder="Select tax group" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Tax</SelectItem>
                      {taxGroups.map((group) => (
                        <SelectItem key={group.group_id} value={group.group_id}>
                          {group.name} ({group.taxes?.map(t => `${t.name} ${t.percentage}%`).join(", ")})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Variations */}
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-gray-900">Variations (Size, Color, etc.)</h3>
                    <Button type="button" variant="outline" size="sm" onClick={addVariation}><Plus size={14} className="mr-1" /> Add Variation</Button>
                  </div>
                  {formData.variations.map((variation, varIndex) => (
                    <div key={variation.variation_id} className="mb-4 p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="flex-1">
                          <Label className="text-[10px] text-gray-400">Variation Type</Label>
                          <Input placeholder="e.g., Size, Color, Flavor" value={variation.name} onChange={(e) => updateVariation(varIndex, "name", e.target.value)} className="bg-white" />
                        </div>
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeVariation(varIndex)} className="text-red-500 mt-5"><Trash2 size={14} /></Button>
                      </div>
                      <div className="space-y-2 ml-4">
                        <p className="text-[10px] text-gray-400 font-medium">Options:</p>
                        {variation.options.map((option, optIndex) => (
                          <div key={option.option_id} className="flex items-end gap-2">
                            <div className="flex-1">
                              <Label className="text-[10px] text-gray-500">Option Name</Label>
                              <Input placeholder="e.g., Large, Red" value={option.value} onChange={(e) => updateVariationOption(varIndex, optIndex, "value", e.target.value)} className="bg-white" />
                            </div>
                            <div className="w-24">
                              <Label className="text-[10px] text-gray-500">Price +/-</Label>
                              <Input type="number" step="0.01" placeholder="0.00" value={option.price_adjustment} onChange={(e) => updateVariationOption(varIndex, optIndex, "price_adjustment", e.target.value)} className="bg-white" />
                            </div>
                            <label className="flex items-center gap-1 text-xs whitespace-nowrap pb-2" title="Available for order">
                              <input type="checkbox" checked={option.is_available !== false} onChange={(e) => updateVariationOption(varIndex, optIndex, "is_available", e.target.checked)} className="rounded accent-green-600" />
                              <span className="text-gray-500">Avail</span>
                            </label>
                            <Button type="button" variant="ghost" size="sm" onClick={() => removeVariationOption(varIndex, optIndex)} className="text-red-500"><X size={14} /></Button>
                          </div>
                        ))}
                        <Button type="button" variant="outline" size="sm" onClick={() => addVariationOption(varIndex)}><Plus size={12} className="mr-1" /> Add Option</Button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Addon Groups */}
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-gray-900">Addon Groups (Toppings, Extras)</h3>
                    <Button type="button" variant="outline" size="sm" onClick={addAddonGroup}><Plus size={14} className="mr-1" /> Add Group</Button>
                  </div>
                  {formData.addon_groups.map((group, grpIndex) => (
                    <div key={group.group_id} className="mb-4 p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-end gap-2 mb-3">
                        <div className="flex-1">
                          <Label className="text-[10px] text-gray-400">Group Name</Label>
                          <Input placeholder="e.g., Toppings, Sauces" value={group.name} onChange={(e) => updateAddonGroup(grpIndex, "name", e.target.value)} className="bg-white" />
                        </div>
                        <div className="w-16">
                          <Label className="text-[10px] text-gray-500">Min</Label>
                          <Input type="number" placeholder="0" value={group.min_selection} onChange={(e) => updateAddonGroup(grpIndex, "min_selection", e.target.value)} className="bg-white" title="Minimum selections required" />
                        </div>
                        <div className="w-16">
                          <Label className="text-[10px] text-gray-500">Max</Label>
                          <Input type="number" placeholder="10" value={group.max_selection} onChange={(e) => updateAddonGroup(grpIndex, "max_selection", e.target.value)} className="bg-white" title="Maximum selections allowed" />
                        </div>
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeAddonGroup(grpIndex)} className="text-red-500"><Trash2 size={14} /></Button>
                      </div>
                      <div className="space-y-2 ml-4">
                        <p className="text-[10px] text-gray-400 font-medium">Addons:</p>
                        {group.addons.map((addon, addonIndex) => (
                          <div key={addon.addon_id} className="flex items-end gap-2">
                            <div className="flex-1">
                              <Label className="text-[10px] text-gray-500">Addon Name</Label>
                              <Input placeholder="e.g., Extra Cheese" value={addon.name} onChange={(e) => updateAddon(grpIndex, addonIndex, "name", e.target.value)} className="bg-white" />
                            </div>
                            <div className="w-20">
                              <Label className="text-[10px] text-gray-500">Price</Label>
                              <Input type="number" step="0.01" placeholder="0.00" value={addon.price} onChange={(e) => updateAddon(grpIndex, addonIndex, "price", e.target.value)} className="bg-white" disabled={addon.is_free} />
                            </div>
                            <label className="flex items-center gap-1 text-xs whitespace-nowrap pb-2">
                              <input type="checkbox" checked={addon.is_free} onChange={(e) => updateAddon(grpIndex, addonIndex, "is_free", e.target.checked)} className="rounded" /> Free
                            </label>
                            <Button type="button" variant="ghost" size="sm" onClick={() => removeAddon(grpIndex, addonIndex)} className="text-red-500"><X size={14} /></Button>
                          </div>
                        ))}
                        <Button type="button" variant="outline" size="sm" onClick={() => addAddon(grpIndex)}><Plus size={12} className="mr-1" /> Add Addon</Button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Flags */}
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-4">Options</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${formData.is_available ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                        <Label className="font-medium">Available for Order</Label>
                      </div>
                      <Switch checked={formData.is_available} onCheckedChange={(checked) => setFormData({ ...formData, is_available: checked })} />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${formData.is_vegetarian ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                        <Label className="font-medium">Vegetarian</Label>
                      </div>
                      <Switch checked={formData.is_vegetarian} onCheckedChange={(checked) => setFormData({ ...formData, is_vegetarian: checked })} />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${formData.is_bestseller ? 'bg-yellow-500' : 'bg-gray-300'}`}></div>
                        <Label className="font-medium">Bestseller</Label>
                      </div>
                      <Switch checked={formData.is_bestseller} onCheckedChange={(checked) => setFormData({ ...formData, is_bestseller: checked })} />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Dumbbell size={14} className={formData.is_gym_special ? 'text-emerald-600' : 'text-gray-300'} />
                        <Label className="font-medium">Gym Special</Label>
                      </div>
                      <Switch checked={formData.is_gym_special} onCheckedChange={(checked) => setFormData({ ...formData, is_gym_special: checked })} data-testid="menu-form-gym-special" />
                    </div>
                  </div>
                </div>

                <Button type="submit" className="w-full bg-green-500 hover:bg-green-600" disabled={formLoading}>
                  {formLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : editingItem ? "Update Item" : "Add Item"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>}
          </div>
        </div>

        {/* Selected kitchen info banner for managers */}
        {isManager && selectedKitchenId && (() => {
          const kit = kitchens.find(k => k.kitchen_id === selectedKitchenId);
          return kit ? (
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 mb-4 flex items-center gap-3" data-testid="manager-kitchen-banner">
              <ChefHat size={18} className="text-orange-600" />
              <div>
                <p className="text-sm font-bold text-orange-800">{kit.kitchen_code} — {kit.city}{kit.locality ? ` - ${kit.locality}` : ""}</p>
                <p className="text-xs text-orange-600">{menuItems.length} menu items</p>
              </div>
            </div>
          ) : null;
        })()}

        {/* Advanced Filter Bar */}
        <div className="mb-4 sm:mb-6 space-y-3" data-testid="menu-filter-bar">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                type="text"
                placeholder="Search..."
                value={filters.name}
                onChange={(e) => setFilters({ ...filters, name: e.target.value })}
                className="pl-9 bg-white text-sm"
                data-testid="menu-filter-name"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className={`gap-1.5 text-xs ${activeFilterCount > 0 ? "border-green-500 text-green-600" : ""}`}
              data-testid="menu-filter-toggle"
            >
              <SlidersHorizontal size={14} />
              <span className="hidden sm:inline">Filters</span>
              {activeFilterCount > 0 && (
                <span className="bg-green-500 text-white text-[9px] rounded-full w-4 h-4 flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </Button>
            {activeFilterCount > 0 && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-gray-500" data-testid="menu-filter-clear">
                Clear
              </Button>
            )}
          </div>
          {showFilters && (
            <div className="bg-white border border-gray-200 rounded-xl p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 animate-fade-in">
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Category</Label>
                <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
                  <SelectTrigger className="bg-white h-9 text-sm" data-testid="menu-filter-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {uniqueCategories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Status</Label>
                <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                  <SelectTrigger className="bg-white h-9 text-sm" data-testid="menu-filter-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="unavailable">Unavailable</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Type</Label>
                <Select value={filters.type} onValueChange={(v) => setFilters({ ...filters, type: v })}>
                  <SelectTrigger className="bg-white h-9 text-sm" data-testid="menu-filter-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="veg">Vegetarian</SelectItem>
                    <SelectItem value="nonveg">Non-Veg</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Bestseller</Label>
                <Select value={filters.bestseller} onValueChange={(v) => setFilters({ ...filters, bestseller: v })}>
                  <SelectTrigger className="bg-white h-9 text-sm" data-testid="menu-filter-bestseller">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Price Min</Label>
                <Input type="number" placeholder="0" value={filters.priceMin} onChange={(e) => setFilters({ ...filters, priceMin: e.target.value })} className="bg-white h-9 text-sm" data-testid="menu-filter-price-min" />
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Price Max</Label>
                <Input type="number" placeholder="999" value={filters.priceMax} onChange={(e) => setFilters({ ...filters, priceMax: e.target.value })} className="bg-white h-9 text-sm" data-testid="menu-filter-price-max" />
              </div>
            </div>
          )}
        </div>

        {/* Menu Items Grouped by Category */}
        {loading ? (
          <div className="space-y-4">{[1, 2, 3].map((i) => <div key={i} className="skeleton h-20 w-full rounded-xl"></div>)}</div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-gray-100"><p className="text-gray-500">No menu items found</p></div>
        ) : (
          <div className="space-y-4">
            {sortedCategoryNames.map((catName) => (
              <div key={catName} className="bg-white rounded-2xl border border-gray-100 overflow-hidden" data-testid={`category-group-${catName.toLowerCase().replace(/\s+/g, "-")}`}>
                {/* Category Header */}
                <button
                  className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                  onClick={() => toggleCategory(catName)}
                  data-testid={`category-toggle-${catName.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="flex items-center gap-2">
                    {collapsedCategories[catName] ? <ChevronRight size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
                    <span className="font-semibold text-gray-700">{catName}</span>
                    <span className="text-[10px] text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full">{groupedItems[catName].length}</span>
                  </div>
                </button>

                {/* Items — Card view (mobile) + Table view (desktop) */}
                {!collapsedCategories[catName] && (
                  <>
                    {/* Desktop Table */}
                    <div className="hidden md:block overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50/50 border-b border-gray-100">
                          <tr>
                            <th className="text-left p-4 text-gray-500 font-medium text-sm">Item</th>
                            <th className="text-left p-4 text-gray-500 font-medium text-sm">Price</th>
                            <th className="text-left p-4 text-gray-500 font-medium text-sm">Rating</th>
                            <th className="text-left p-4 text-gray-500 font-medium text-sm">Nutrition</th>
                            <th className="text-left p-4 text-gray-500 font-medium text-sm">Status</th>
                            <th className="text-right p-4 text-gray-500 font-medium text-sm">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {groupedItems[catName].map((item) => (
                            <tr key={item.item_id} className="hover:bg-gray-50">
                              <td className="p-4">
                                <div className="flex items-center gap-3">
                                  <img src={item.image_path ? `${API.replace("/api", "")}/api/files/${item.image_path}` : item.image_url || "https://images.unsplash.com/photo-1606757819934-d61a9f7279d5?w=60"} alt={item.name} className="w-12 h-12 rounded-lg object-cover" />
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <p className="font-medium text-gray-900">{item.name}</p>
                                      {item.is_bestseller && <Star size={14} className="text-yellow-500 fill-yellow-500" />}
                                      {item.is_vegetarian && <Leaf size={14} className="text-green-500" />}
                                    </div>
                                    {item.superfood_angle && <p className="text-green-600 text-xs flex items-center gap-1"><Leaf size={10} /> {item.superfood_angle.substring(0, 30)}...</p>}
                                  </div>
                                </div>
                              </td>
                              <td className="p-4"><span className="font-medium text-gray-900">{symbol}{Math.round(item.base_price || 0)}</span></td>
                              <td className="p-4">
                                {item.avg_rating ? (
                                  <div className="flex items-center gap-1">
                                    <Star size={13} className="text-amber-400 fill-amber-400" />
                                    <span className="text-sm font-semibold text-amber-700">{item.avg_rating}</span>
                                    <span className="text-[9px] text-gray-500">({item.review_count})</span>
                                  </div>
                                ) : <span className="text-gray-400 text-xs">No reviews</span>}
                              </td>
                              <td className="p-4">
                                {item.nutritional_info?.calories ? (
                                  <div className="flex items-center gap-1 text-[10px] text-gray-400">
                                    <Flame size={12} className="text-orange-500" />
                                    {item.nutritional_info.calories} kcal
                                  </div>
                                ) : <span className="text-gray-400 text-xs">Not set</span>}
                              </td>
                              <td className="p-4">
                                <button
                                  onClick={(e) => { e.stopPropagation(); toggleAvailability(item.item_id); }}
                                  className={`px-2 py-1 rounded-full text-xs font-medium cursor-pointer transition-colors ${item.is_available ? "bg-green-100 text-green-700 hover:bg-green-200" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                                  data-testid={`toggle-avail-${item.item_id}`}
                                >
                                  {item.is_available ? "Available" : "Unavailable"}
                                </button>
                              </td>
                              <td className="p-4">
                                {isAdmin && <div className="flex justify-end gap-2">
                                  <Button variant="outline" size="sm" onClick={() => handleDuplicate(item.item_id)} title="Duplicate" data-testid={`duplicate-${item.item_id}`}><Copy size={14} /></Button>
                                  <Button variant="outline" size="sm" onClick={() => openEditDialog(item)} data-testid={`edit-${item.item_id}`}><Pencil size={14} /></Button>
                                  <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(item.item_id)} data-testid={`delete-${item.item_id}`}><Trash2 size={14} /></Button>
                                </div>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile Card View */}
                    <div className="md:hidden divide-y divide-gray-100">
                      {groupedItems[catName].map((item) => (
                        <div key={item.item_id} className="p-3" data-testid={`menu-card-${item.item_id}`}>
                          <div className="flex gap-2.5">
                            <img
                              src={item.image_path ? `${API.replace("/api", "")}/api/files/${item.image_path}` : item.image_url || "https://images.unsplash.com/photo-1606757819934-d61a9f7279d5?w=60"}
                              alt={item.name}
                              className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <h3 className="text-xs font-semibold text-gray-900 truncate">{item.name}</h3>
                                {item.is_bestseller && <Star size={10} className="text-yellow-500 fill-yellow-500 flex-shrink-0" />}
                              </div>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                <span className="text-xs font-bold text-green-600">{symbol}{Math.round(item.base_price || 0)}</span>
                                {item.is_vegetarian && <span className="text-[8px] font-bold text-green-700 bg-green-50 border border-green-200 px-1 rounded">VEG</span>}
                                {item.avg_rating > 0 && (
                                  <span className="flex items-center gap-0.5 text-[9px] text-amber-700"><Star size={8} className="text-amber-400 fill-amber-400" />{item.avg_rating}</span>
                                )}
                              </div>
                              {/* Status + Actions row */}
                              <div className="flex items-center gap-1.5 mt-1.5">
                                <button
                                  onClick={() => toggleAvailability(item.item_id)}
                                  className={`px-2 py-0.5 rounded text-[9px] font-bold ${item.is_available ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-500"}`}
                                  data-testid={`toggle-avail-${item.item_id}`}
                                >
                                  {item.is_available ? "ON" : "OFF"}
                                </button>
                                {isAdmin && (
                                  <>
                                    <button onClick={() => openEditDialog(item)} className="text-[9px] font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded" data-testid={`edit-${item.item_id}`}>Edit</button>
                                    <button onClick={() => handleDuplicate(item.item_id)} className="text-[9px] font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded" data-testid={`duplicate-${item.item_id}`}>Copy</button>
                                    <button onClick={() => handleDelete(item.item_id)} className="text-[9px] font-medium text-red-500 bg-red-50 px-2 py-0.5 rounded" data-testid={`delete-${item.item_id}`}>Del</button>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <MenuImportDialog open={importOpen} onClose={() => setImportOpen(false)} onImported={() => fetchMenu(isManager ? selectedKitchenId : undefined)} />
    </AdminLayout>
  );
}

/* ==================== Menu CSV Import Dialog ==================== */
function MenuImportDialog({ open, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  if (!open) return null;

  const downloadSample = async () => {
    try {
      const r = await fetch(`${API}/admin/menu/import/sample`, { credentials: "include" });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "menu-items-sample.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download sample"); }
  };

  const submit = async () => {
    if (!file) { toast.error("Pick a CSV first"); return; }
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${API}/admin/menu/import`, { method: "POST", credentials: "include", body: fd });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Import failed");
      setResult(data);
      if (data.created > 0) {
        toast.success(`${data.created} items imported`);
        onImported?.();
      } else if (data.failed_count > 0) {
        toast.error(`No items imported. ${data.failed_count} rows failed validation.`);
      }
    } catch (e) {
      toast.error(e.message || "Import failed");
    } finally { setUploading(false); }
  };

  const close = () => { setFile(null); setResult(null); onClose?.(); };

  return (
    <Dialog open onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-lg bg-white max-h-[90vh] overflow-y-auto" data-testid="menu-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileSpreadsheet size={18} /> Bulk Import Menu Items</DialogTitle>
        </DialogHeader>

        <div className="space-y-5 text-sm">
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-amber-800">
            <p className="font-semibold text-xs mb-1.5">How to prepare your CSV</p>
            <ul className="list-disc pl-4 space-y-1 text-[11px] text-amber-900">
              <li><b>name</b>, <b>description</b>, <b>base_price</b> are required on every row.</li>
              <li><b>category</b> & <b>subcategory</b> match by name (case-insensitive). Missing ones are auto-created.</li>
              <li><b>tax_group</b> must match an existing tax group name exactly. Unknown names → item created without tax.</li>
              <li><b>is_vegetarian</b>, <b>is_available</b>, <b>is_bestseller</b> accept <code>true / false</code> (or 1 / 0).</li>
              <li>Nutrition columns (<code>calories_kcal</code>, <code>protein_g</code>, <code>carbs_g</code>, <code>fat_g</code>, <code>fiber_g</code>) are optional — leave blank to skip.</li>
              <li><b>image_url</b> is optional — upload images for each item individually after import.</li>
              <li>Save your file as <b>UTF-8 CSV</b> (in Excel: File → Save As → CSV UTF-8).</li>
            </ul>
          </div>

          <Button variant="outline" onClick={downloadSample} className="w-full" data-testid="menu-import-download-sample">
            <FileDown size={14} className="mr-2" /> Download Sample CSV
          </Button>

          <div>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
              data-testid="menu-import-file-input"
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="w-full border-2 border-dashed border-gray-200 rounded-xl p-6 text-center hover:border-green-400 hover:bg-green-50 transition-colors"
              data-testid="menu-import-picker"
            >
              <Upload size={18} className="text-gray-400 mx-auto mb-1" />
              <p className="text-xs font-medium text-gray-700">{file ? file.name : "Click to select a CSV"}</p>
              {file && <p className="text-[10px] text-gray-400 mt-1">{(file.size / 1024).toFixed(1)} KB</p>}
            </button>
          </div>

          {result && (
            <div className="space-y-2 text-xs" data-testid="menu-import-result">
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-green-50 border border-green-100 rounded-lg p-2 text-center">
                  <p className="text-[10px] uppercase text-green-600 font-semibold">Created</p>
                  <p className="text-lg font-bold text-green-700">{result.created}</p>
                </div>
                <div className="bg-red-50 border border-red-100 rounded-lg p-2 text-center">
                  <p className="text-[10px] uppercase text-red-600 font-semibold">Failed</p>
                  <p className="text-lg font-bold text-red-700">{result.failed_count}</p>
                </div>
                <div className="bg-amber-50 border border-amber-100 rounded-lg p-2 text-center">
                  <p className="text-[10px] uppercase text-amber-600 font-semibold">Warnings</p>
                  <p className="text-lg font-bold text-amber-700">{result.warnings_count}</p>
                </div>
              </div>
              {result.failed?.length > 0 && (
                <div className="bg-red-50 border border-red-100 rounded-lg p-2 max-h-32 overflow-y-auto">
                  <p className="text-[10px] font-semibold text-red-800 mb-1">Failed rows:</p>
                  {result.failed.map((f, i) => (
                    <p key={i} className="text-[11px] text-red-700">Line {f.line}: {f.error}</p>
                  ))}
                </div>
              )}
              {result.warnings?.length > 0 && (
                <div className="bg-amber-50 border border-amber-100 rounded-lg p-2 max-h-32 overflow-y-auto">
                  <p className="text-[10px] font-semibold text-amber-800 mb-1">Warnings:</p>
                  {result.warnings.map((w, i) => (
                    <p key={i} className="text-[11px] text-amber-900">{w}</p>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex gap-2">
            <Button variant="outline" onClick={close} className="flex-1" data-testid="menu-import-cancel">Close</Button>
            <Button onClick={submit} disabled={!file || uploading} className="flex-1 bg-green-600 hover:bg-green-700" data-testid="menu-import-submit">
              {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing…</> : <><Upload size={14} className="mr-2" /> Import</>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
